import { useCallback, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { ChevronLeft, History } from 'lucide-react'
import { useT } from '@/i18n'
import { PageHeader } from '@/shell/AppShell'
import { DataTable } from '@/components/DataTable'
import type { AdminColumn, FetchParams, FetchResult } from '@/components/DataTable/types'
import { fetchAudit, type AuditRow } from '@/lib/adapters'
import { PERMISSIONS } from '@/lib/permissions'

/**
 * A14.08 — Feature-flag / remote-config o'zgarishlar jurnali.
 *
 * Alohida mutatsiya-endpoint YO'Q (flaglar hozir read-only, A14.03 kutadi) —
 * shuning uchun bu ekran mavjud audit-jurnalini (`GET /admin/audit`) FILTRLAB,
 * faqat flag/config-tegishli yozuvlarni ko'rsatadi. Yangi flag-mutatsiyalari
 * qo'shilgach, ular shu yerda avtomatik chiqadi (backend audit yozadi).
 * Read-only, AUDIT_VIEW-gated (flaglar bilan bir xil ruxsat).
 */
const FLAG_RE = /flag|feature_|remote.?config|setting/i

async function fetchFlagChanges(params: FetchParams): Promise<FetchResult<AuditRow>> {
  // Pull a recent audit window, then keep only flag/config-related entries and
  // re-paginate the filtered set to the requested page.
  const chunk = await fetchAudit({ ...params, page: 1, pageSize: 500 })
  const rows = chunk.rows.filter((a) => FLAG_RE.test(`${a.action} ${a.target ?? ''} ${a.detail ?? ''}`))
  const start = (params.page - 1) * params.pageSize
  return { rows: rows.slice(start, start + params.pageSize), total: rows.length }
}

export function FeatureFlagChangeLog() {
  const t = useT()

  const columns = useMemo<AdminColumn<AuditRow>[]>(
    () => [
      { key: 'created_at', header: t.col.time, type: 'date', width: 150 },
      { key: 'admin', header: t.col.staff, type: 'text', width: 160 },
      {
        key: 'action',
        header: t.col.action,
        type: 'text',
        width: 180,
        render: (r) => <span className="font-mono text-sm text-primary">{r.action}</span>,
      },
      {
        key: 'target',
        header: t.flags.key,
        type: 'text',
        minWidth: 200,
        render: (r) => <span className="font-mono text-sm text-secondary">{r.target ?? '—'}</span>,
      },
      { key: 'detail', header: t.col.detail, type: 'longtext', minWidth: 220 },
    ],
    [t],
  )

  const fetcher = useCallback(fetchFlagChanges, [])

  return (
    <>
      <PageHeader
        title={t.flags.changelog}
        screenId="A14.08"
        actions={
          <Link
            to="/flags"
            className="inline-flex items-center gap-1 rounded-md border border-border-default bg-surface-2 px-2.5 py-1 text-sm text-secondary hover:text-primary"
          >
            <ChevronLeft size={13} />
            {t.flags.title}
          </Link>
        }
      />
      <DataTable<AuditRow>
        screenId="A14.08"
        permission={PERMISSIONS.AUDIT_VIEW}
        columns={columns}
        fetcher={fetcher}
        rowId={(r) => String(r.id ?? `${r.created_at}:${r.target ?? ''}`)}
        exportable
        emptyState={{ icon: <History size={28} strokeWidth={1.5} /> }}
      />
    </>
  )
}
